import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS, BrandS, LabelS } from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DailyActionSubTask } from './DailyActionSubTask/index.tsx';
import { DailyActionTaskRewards } from './DailyActionTaskRewards/index.tsx';
import type { DailyActionTask_taskInterface } from './__generated__/index.graphql.ts';
import background from './assets/background.webp';

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  text-align: center;
  justify-content: space-between;
  gap: var(--double-unit);
  padding: var(--triple-unit) var(--double-unit) var(--double-unit);
  > :first-child {
    flex: 2 0 auto;
  }
  > :last-child {
    flex: 1 0 0;
  }
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;
const Root = styled(Vertical)`
  padding: var(--double-unit);
`;

const TitleBlock = styled(Vertical)`
  align-items: flex-start;
`;

type Props = {
  task: DailyActionTask_taskInterface;
  onDecisivePlayerPickerClick: Nullable<() => void>;
  onDropChallengeClick: Nullable<() => void>;
  showTitle?: boolean;
};

export const DailyActionTask = ({
  task,
  onDropChallengeClick,
  onDecisivePlayerPickerClick,
  showTitle,
  children,
}: PropsWithChildren<Props>) => {
  const { title, description } = task;
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const { subtasks } = isType(task, 'DailyActionTask')
    ? task
    : { subtasks: [] };

  return (
    <Bezel
      borderColor="var(--c-blue-400)"
      background={`url(${background}) no-repeat top / cover`}
    >
      <Root gap={3}>
        <Header>
          {!!showTitle && (
            <TitleBlock gap={0}>
              <Horizontal center>
                <BrandS>{title}</BrandS>
                {!!dailyTasksExpiresAt && (
                  <Tooltip
                    title={
                      <FormattedMessage
                        defaultMessage="Daily missions reset at {time}"
                        values={{
                          time: (
                            <FormattedDate
                              value={dailyTasksExpiresAt}
                              day="numeric"
                              month="long"
                              year="numeric"
                              hour="numeric"
                              minute="numeric"
                            />
                          ),
                        }}
                      />
                    }
                  >
                    <Horizontal gap={0.5}>
                      <FontAwesomeIcon
                        icon={iconClock}
                        color="var(--c-yellow-400)"
                      />
                      <LabelS bold color="var(--c-yellow-400)">
                        <TimeLeft time={dailyTasksExpiresAt} options={{}} />
                      </LabelS>
                    </Horizontal>
                  </Tooltip>
                )}
              </Horizontal>
              <BodyS color="var(--c-nd-500)">{description}</BodyS>
            </TitleBlock>
          )}

          <DailyActionTaskRewards task={task} />
        </Header>
        <Vertical>
          {subtasks.map(subtask => (
            <DailyActionSubTask
              key={subtask.id}
              task={subtask}
              onDropChallengeClick={onDropChallengeClick}
              onDecisivePlayerPickerClick={onDecisivePlayerPickerClick}
            />
          ))}
          {children}
        </Vertical>
      </Root>
    </Bezel>
  );
};

DailyActionTask.fragments = {
  taskInterface: gql`
    fragment DailyActionTask_taskInterface on TaskInterface {
      id
      title
      description
      ... on DailyActionTask {
        id
        subtasks {
          id
          ...DailyActionSubTask_taskInterface
        }
      }
      ...DailyActionTaskRewards_taskInterface
    }
    ${DailyActionSubTask.fragments.taskInterface}
    ${DailyActionTaskRewards.fragments.taskInterface}
  ` as TypedDocumentNode<DailyActionTask_taskInterface>,
};
